/**
 * Shared primitive schemas used across all route groups.
 *
 * Workers-and-browser-safe. Do not import any Node-only modules from this file.
 */
import { z } from "zod";
/**
 * RFC 4122 UUID — 8-4-4-4-12 hex chars. Zod ships a built-in `.uuid()` validator;
 * we wrap it to give the schema a stable name for OpenAPI registration.
 */
export const UuidSchema = z.string().uuid().describe("RFC 4122 UUID");
/**
 * ISO 8601 timestamp with timezone (e.g., `2026-04-25T12:34:56.789Z`).
 *
 * We use `.datetime({ offset: true })` so both `Z` and explicit offsets parse.
 */
export const IsoTimestampSchema = z
    .string()
    .datetime({ offset: true })
    .describe("ISO 8601 timestamp");
/**
 * Tenant slug — lowercase alphanumeric + hyphens, 3-32 chars.
 *
 * Used in URLs and as the human-readable handle on the tenant record.
 */
export const TenantSlugSchema = z
    .string()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/)
    .describe("Lowercase tenant slug, 3-32 chars");
/**
 * Pagination query parameters. Cursor-based to keep response shapes stable
 * even when underlying ordering changes.
 */
export const PaginationQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * Wraps a list response with pagination metadata.
 *
 * Usage: `paginatedResponse(MyItemSchema)` → `{ items: MyItem[], next_cursor?: string }`.
 */
export function paginatedResponse(itemSchema) {
    return z.object({
        items: z.array(itemSchema),
        next_cursor: z.string().optional(),
    });
}
/**
 * Idempotency key header value. Up to 128 chars; tenant-supplied; we don't
 * inspect the format.
 */
export const IdempotencyKeySchema = z
    .string()
    .min(1)
    .max(128)
    .describe("Caller-supplied idempotency key (header `Idempotency-Key`)");
//# sourceMappingURL=common.js.map